import { makeAutoObservable, runInAction } from 'mobx'
import { apiClient } from '@/services/api'
import type {
  AgentTeam,
  AgentTeamCreate,
  AgentTeamUpdate,
  TeamMemberCreate,
  PaginatedResponse,
  TeamCoordinationStrategy,
  TeamStatus,
} from '@/types'

class TeamStore {
  teams: AgentTeam[] = []
  selectedTeam: AgentTeam | null = null
  isLoading = false
  error: string | null = null

  // Pagination
  currentPage = 1
  pageSize = 10
  totalTeams = 0

  // Filters
  searchQuery = ''
  strategyFilter: TeamCoordinationStrategy | 'all' = 'all'
  statusFilter: TeamStatus | 'all' = 'all'

  constructor() {
    makeAutoObservable(this)
  }

  // Computed values
  get filteredTeams() {
    const teams = this.teams || []
    return teams.filter((team) => {
      const matchesSearch =
        !this.searchQuery ||
        team.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        team.description?.toLowerCase().includes(this.searchQuery.toLowerCase())

      const matchesStrategy =
        this.strategyFilter === 'all' || team.strategy === this.strategyFilter

      const matchesStatus =
        this.statusFilter === 'all' || team.status === this.statusFilter

      return matchesSearch && matchesStrategy && matchesStatus
    })
  }

  get hasActiveFilters() {
    return (
      this.searchQuery !== '' ||
      this.strategyFilter !== 'all' ||
      this.statusFilter !== 'all'
    )
  }

  // Actions
  async fetchTeams(workspaceId?: string) {
    this.isLoading = true
    this.error = null

    try {
      const response: PaginatedResponse<AgentTeam> = await apiClient.getTeams({
        workspace_id: workspaceId,
        skip: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
      })

      runInAction(() => {
        this.teams = response.items
        this.totalTeams = response.total
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.teams = []
        this.error = error.response?.data?.detail || 'Failed to fetch teams'
        this.isLoading = false
      })
    }
  }

  async fetchTeamById(id: string) {
    this.isLoading = true
    this.error = null

    try {
      const team = await apiClient.getTeam(id)

      runInAction(() => {
        this.selectedTeam = team
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch team'
        this.isLoading = false
      })
    }
  }

  async createTeam(data: AgentTeamCreate) {
    this.isLoading = true
    this.error = null

    try {
      const team = await apiClient.createTeam(data)

      runInAction(() => {
        this.teams = [team, ...this.teams]
        this.totalTeams += 1
        this.isLoading = false
      })

      return team
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to create team'
        this.isLoading = false
      })
      throw error
    }
  }

  async updateTeam(id: string, data: AgentTeamUpdate) {
    this.isLoading = true
    this.error = null

    try {
      const updatedTeam = await apiClient.updateTeam(id, data)

      runInAction(() => {
        this.teams = this.teams.map((team) =>
          team.id === id ? updatedTeam : team
        )
        if (this.selectedTeam?.id === id) {
          this.selectedTeam = updatedTeam
        }
        this.isLoading = false
      })

      return updatedTeam
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to update team'
        this.isLoading = false
      })
      throw error
    }
  }

  async deleteTeam(id: string) {
    this.isLoading = true
    this.error = null

    try {
      await apiClient.deleteTeam(id)

      runInAction(() => {
        this.teams = this.teams.filter((team) => team.id !== id)
        this.totalTeams -= 1
        if (this.selectedTeam?.id === id) {
          this.selectedTeam = null
        }
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to delete team'
        this.isLoading = false
      })
      throw error
    }
  }

  async addMember(teamId: string, data: TeamMemberCreate) {
    this.isLoading = true
    this.error = null

    try {
      const member = await apiClient.addTeamMember(teamId, data)

      // Refresh team to get updated members list
      await this.fetchTeamById(teamId)

      runInAction(() => {
        this.isLoading = false
      })

      return member
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to add team member'
        this.isLoading = false
      })
      throw error
    }
  }

  async removeMember(teamId: string, memberId: string) {
    this.isLoading = true
    this.error = null

    try {
      await apiClient.removeTeamMember(teamId, memberId)

      // Refresh team to get updated members list
      await this.fetchTeamById(teamId)

      runInAction(() => {
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to remove team member'
        this.isLoading = false
      })
      throw error
    }
  }

  // Filter actions
  setSearchQuery(query: string) {
    this.searchQuery = query
  }

  setStrategyFilter(strategy: TeamCoordinationStrategy | 'all') {
    this.strategyFilter = strategy
  }

  setStatusFilter(status: TeamStatus | 'all') {
    this.statusFilter = status
  }

  clearFilters() {
    this.searchQuery = ''
    this.strategyFilter = 'all'
    this.statusFilter = 'all'
  }

  // Pagination actions
  setPage(page: number) {
    this.currentPage = page
  }

  setPageSize(size: number) {
    this.pageSize = size
    this.currentPage = 1 // Reset to first page
  }

  // Utility actions
  clearError() {
    this.error = null
  }

  clearSelectedTeam() {
    this.selectedTeam = null
  }
}

export const teamStore = new TeamStore()
